import { BookDto } from '@hermes/domains'
import { GetBookDetailsResponse, Pagination } from '@hermes/types/'
import { BookInterface, RelevantBook, RelevantSale, ReviewInterface } from '@hermes/types/src'
import { getCacheRevalidateTime } from '@lib/utils/getCacheRevalidate'
import { getPublicConfigVariable } from '@lib/utils/getConfigVariable'
import { Region } from '@lib/utils/getRegionFromLocale'
import { notFound } from 'next/navigation'

const legacyApiPath = getPublicConfigVariable('LEGACY_API_URL')
const hermesApiPath = getPublicConfigVariable('HERMES_API_URL')

export const getBookBySlug = async ({ region, slug }: { region: Region; slug: string }) => {
  const response = await fetch(`${hermesApiPath}/v1/${region}/components/book-details-page/${slug}`, {
    next: { revalidate: getCacheRevalidateTime('bookDetails') }
  })
  if (!response.ok) {
    console.error('Failed to get book by slug with hermes API.', response.statusText)
    notFound()
  }
  const parsedResponse: GetBookDetailsResponse = await response.json()

  if (!parsedResponse) {
    console.error('Failed to get book by slug with hermes API. No data returned.')
    notFound()
  }
  return parsedResponse.data
}

export const getBookReviews = async ({
  bookId,
  country
}: {
  bookId: number
  country: string
}): Promise<{
  data: {
    array: ReviewInterface[]
  }
  code?: number
  message?: string
}> =>
  fetch(`${legacyApiPath}/book/${bookId}/reviews`, {
    headers: {
      country
    }
  }).then((r) => r.json())

export const addToWaitingList = async ({
  availableNew,
  availableUsed,
  book,
  token,
  country
}: {
  availableNew: boolean
  availableUsed: boolean
  book: number
  token: string
  country: string
}): Promise<{
  code?: number
  message?: string
  data: {
    BookId: number
    UserId: number
  }
}> =>
  fetch(`${legacyApiPath}/waitinglists`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      country
    },
    body: JSON.stringify({
      book,
      availableNew,
      availableUsed
    })
  }).then((r) => r.json())

export const addBookReview = ({
  review,
  reviewId,
  bookId,
  token,
  country
}: {
  review: string
  reviewId?: number
  bookId: number
  token: string
  country: string
}): Promise<{
  data: {
    createdAt: string
    review: string
    id: number
  }
  code?: number
  message?: string
}> =>
  fetch(`${legacyApiPath}/book/${bookId}/reviews${reviewId ? `/${reviewId}` : ''}`, {
    method: reviewId ? 'PUT' : 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      country
    },
    body: JSON.stringify({ review })
  }).then((r) => r.json())

export const deleteBookReview = ({
  reviewId,
  bookId,
  token,
  country
}: {
  reviewId: number
  bookId: number
  token: string
  country: string
}): Promise<{
  code?: number
  message?: string
}> =>
  fetch(`${legacyApiPath}/book/${bookId}/reviews/${reviewId}`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      country
    }
  }).then((r) => r.json())

export const addBookRating = ({
  rate,
  token,
  bookId,
  country
}: {
  rate: number
  token: string
  bookId: number
  country: string
}): Promise<{
  data: {
    createdAt: Date
    rate: number
  }
  code?: number
  message?: string
}> =>
  fetch(`${legacyApiPath}/book/${bookId}/ratings`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      country
    },
    body: JSON.stringify({ rate })
  }).then((r) => r.json())

export const fetchAuthorBooks = ({
  bookId,
  authorId,
  country,
  page = 0,
  perPage = 12
}: {
  bookId: number
  authorId: number
  country: string
  page?: number
  perPage?: number
}): Promise<{
  data: {
    array: BookInterface[]
    bookSeries: BookInterface[]
    pagination: Pagination
  }
  code?: number
  message?: string
}> =>
  fetch(`${legacyApiPath}/authors/${authorId}/books?book=${bookId}&page=${page}&perPage=${perPage}`, {
    headers: {
      country
    }
  }).then((r) => r.json())

export const fetchSuggestedBooks = ({
  country,
  bookId
}: {
  country: string
  bookId: number
}): Promise<{
  data: {
    array: BookInterface[]
  }
  code?: number
  message?: string
}> =>
  fetch(`${legacyApiPath}/book/${bookId}/suggested`, {
    headers: {
      country
    }
  }).then((r) => r.json())

export const fetchRecommendedSeller = async ({
  bookId,
  country
}: {
  bookId: number
  country: string
}): Promise<{
  data: {
    sale: RelevantSale
    salesCollection: RelevantBook[]
  }
  code?: number
  message?: string
}> =>
  fetch(`${legacyApiPath}/book/recommendedSeller/${bookId}`, {
    headers: {
      country
    }
  }).then((r) => r.json())

export const getBooksByIsbn = async ({
  isbn13,
  region
}: {
  isbn13: string[]
  region: Region
}): Promise<{
  data: BookDto[]
}> => {
  const apiUrl = getPublicConfigVariable('HERMES_API_URL')
  const response = await fetch(`${apiUrl}/v1/${region}/books/search?size=50&isbn13=${isbn13.join(',')}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    next: { revalidate: getCacheRevalidateTime('books') }
  })

  if (!response.ok) {
    throw new Error('Failed to fetch books by ISBN')
  }

  return response.json()
}
